import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  type Language,
  type LanguageConfig,
  languages,
  defaultLanguage,
  LANGUAGE_STORAGE_KEY
} from '../lib/i18n'

/** Language context value */
interface LanguageContextValue {
  /** Current language code */
  language: Language
  /** Set the language */
  setLanguage: (lang: Language) => void
  /** Available languages */
  languages: LanguageConfig[]
  /** Current language config */
  currentLanguageConfig: LanguageConfig
  /** Check if current language matches */
  isLanguage: (lang: Language) => boolean
}

/** Props for LanguageProvider */
interface LanguageProviderProps {
  children: ReactNode
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

/**
 * Get initial language from localStorage or browser settings
 */
function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return defaultLanguage

  // Check localStorage first
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (stored === 'en' || stored === 'ro') {
    return stored
  }

  // Fall back to browser language
  const browserLang = navigator.language.split('-')[0]
  if (browserLang === 'ro') {
    return 'ro'
  }

  return defaultLanguage
}

/**
 * Language provider for internationalization support
 * Persists preference to localStorage and syncs with i18next
 */
export function LanguageProvider({ children }: LanguageProviderProps): JSX.Element {
  const { i18n } = useTranslation()
  const [language, setLanguageState] = useState<Language>(getInitialLanguage)

  // Sync i18n language on mount and when language changes
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language)
    }
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  }, [language, i18n])

  // Listen for language changes from i18n (e.g., from other sources)
  useEffect(() => {
    const handleLanguageChange = (lng: string): void => {
      if ((lng === 'en' || lng === 'ro') && lng !== language) {
        setLanguageState(lng)
      }
    }

    i18n.on('languageChanged', handleLanguageChange)
    return () => {
      i18n.off('languageChanged', handleLanguageChange)
    }
  }, [i18n, language])

  const setLanguage = useCallback((lang: Language): void => {
    setLanguageState(lang)
  }, [])

  const isLanguage = useCallback((lang: Language): boolean => {
    return language === lang
  }, [language])

  const currentLanguageConfig = useMemo((): LanguageConfig => {
    return languages.find(l => l.code === language) || languages[0]
  }, [language])

  const value = useMemo<LanguageContextValue>(() => ({
    language,
    setLanguage,
    languages,
    currentLanguageConfig,
    isLanguage
  }), [language, setLanguage, currentLanguageConfig, isLanguage])

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

/**
 * Hook to access language context
 * @throws Error if used outside LanguageProvider
 */
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export default LanguageContext
